import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import axiosPost from '../../../helpers/axios-helper';
import config from '../../../constants/config';

/**
 * Community App groups. The set is fixed here: members can join but not create
 * one. A group is hidden rather than deleted, so its posts are never lost.
 */

const EMPTY = { id: 0, name: '', description: '', image: '', sort_order: 0, active: 1 };

export default function ConnectGroupsDashboard() {
  const [cookies] = useCookies(['auth_token']);
  const token = cookies.auth_token;
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  const [saveError, setSaveError] = useState('');
  const [uploading, setUploading] = useState(false);
  const imageRef = useRef(null);

  const load = () => {
    axiosPost('groups/list', { 'Content-Type': 'application/json' }, { token }, (resp) => {
      if (resp?.success) {
        setItems(resp.data.items || []);
        setSaveError('');
      } else {
        setSaveError(resp?.response || 'Could not load groups.');
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('token', token || '');
      const resp = await axios.post(`${config.api.url}groups/upload-image`, fd, {
        headers: { api: config.api.key, 'Content-Type': 'multipart/form-data' },
      });
      if (resp?.data?.success) set({ image: resp.data.data.url });
      else setSaveError(resp?.data?.response || 'The upload was refused.');
    } catch (err) {
      setSaveError('Could not upload that image.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveError('');
    if (!formData.name.trim()) {
      setSaveError('A group needs a name.');
      return;
    }
    const payload = { ...formData, token, sort_order: parseInt(formData.sort_order || 0, 10) };
    axiosPost('groups/update', { 'Content-Type': 'application/json' }, payload, (resp) => {
      if (resp?.success) {
        setIsEditing(false);
        load();
      } else {
        setSaveError(resp?.response || 'Could not save that group.');
      }
    });
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Community Groups</h2>
        {!isEditing && (
          <button
            onClick={() => { setFormData(EMPTY); setSaveError(''); setIsEditing(true); }}
            className="DashboardButton"
          >
            <FaPlus /> Add Group
          </button>
        )}
      </div>

      {saveError && <p style={{ color: '#d32f2f' }}>{saveError}</p>}

      {isEditing ? (
        <form onSubmit={handleSubmit}>
          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Name</label>
            <input type="text" className="DashboardFormInput" value={formData.name} onChange={(e) => set({ name: e.target.value })} />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Description</label>
            <textarea className="DashboardFormInput" rows={4} value={formData.description} onChange={(e) => set({ description: e.target.value })} />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Cover image</label>
            {formData.image && (
              <div style={{ marginBottom: 8 }}>
                <img src={formData.image} alt="" style={{ maxWidth: 240, maxHeight: 120, objectFit: 'cover' }} />
              </div>
            )}
            <input ref={imageRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={(e) => { uploadImage(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" className="DashboardButtonSecondary" disabled={uploading} onClick={() => imageRef.current.click()} style={{ marginRight: 8 }}>
              {uploading ? 'Uploading...' : 'Upload image'}
            </button>
            {formData.image && (
              <button type="button" className="DashboardButtonSecondary" onClick={() => set({ image: '' })}>Remove</button>
            )}
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Order</label>
            <input type="number" className="DashboardFormInput" value={formData.sort_order} onChange={(e) => set({ sort_order: e.target.value })} />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={!!formData.active} onChange={(e) => set({ active: e.target.checked ? 1 : 0 })} />
              Show in the Community App
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="DashboardButtonSecondary" onClick={() => setIsEditing(false)}>Cancel</button>
            <button type="submit" className="DashboardButton" disabled={uploading}>
              {formData.id ? 'Update Group' : 'Add Group'}
            </button>
          </div>
        </form>
      ) : (
        <table className="DashboardTable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Members</th>
              <th>Status</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={5} style={{ color: '#667085' }}>No groups yet.</td>
              </tr>
            )}
            {items.map((group) => (
              <tr key={group.id}>
                <td>{group.name}</td>
                <td>{group.member_count}</td>
                <td>{group.active ? 'Live' : 'Hidden'}</td>
                <td>{group.sort_order}</td>
                <td>
                  <button
                    onClick={() => { setFormData(group); setSaveError(''); setIsEditing(true); }}
                    className="DashboardButtonSecondary"
                  >
                    <FaEdit /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
